import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, INLINES} from '@contentful/rich-text-types'
import {Pillar} from '@primer/react-brand/esm'
import type {PrimerComponentPillar} from '../../../schemas/contentful/contentTypes/primerComponentPillar'
import {getAnalyticsEvent, documentToPlainTextString} from '../../../lib/utils/analytics'
import {getImageSources} from '../../../lib/utils/images'
import {ContentfulInlineFootnote} from '../ContentfulFootnotes/ContentfulInlineFootnote'
import {getPrimerIcon} from '../../../lib/utils/icons'
import {renderSingleLineRichText, codeMarkRenderer} from '../shared/RichTextFieldRenderer'

export type ContentfulPillarProps = {
  component: PrimerComponentPillar

  /**
   * The props below do not have a corresponding field in Contentful:
   */
  className?: string
}

export type ContentfulPillarContentProps = {
  component: PrimerComponentPillar
  className?: string
}

export const ContentfulPillarContent = ({component: {fields}, className}: ContentfulPillarContentProps) => {
  const {align, icon, image, heading, headingRich, headingLevel, description, link} = fields
  const plainTextHeading = heading ?? (headingRich ? documentToPlainTextString(headingRich) : '')
  const Octicon = getPrimerIcon(icon)

  return (
    <Pillar className={className} align={align}>
      {Octicon && (
        <Pillar.Icon
          data-testid={`${icon}-${plainTextHeading}`}
          icon={
            // eslint-disable-next-line react-hooks/static-components
            <Octicon />
          }
        />
      )}

      {image && (
        <Pillar.Image
          // Setting verticalAlign: 'bottom' on the image as a workaround for Primer Brand bug:
          // https://github.com/primer/brand/issues/911. This should be removed once the bug is fixed.
          style={{verticalAlign: 'bottom'}}
          as="picture"
          src={`${image.fields.file.url}?fm=webp`}
          sources={getImageSources(image.fields.file.url, {maxWidth: 640})}
          alt={image.fields.description || ''}
        />
      )}

      <Pillar.Heading as={headingLevel}>{headingRich ? renderSingleLineRichText(headingRich) : heading}</Pillar.Heading>

      <Pillar.Description>
        {documentToReactComponents(description, {
          renderMark: codeMarkRenderer,
          renderNode: {
            [BLOCKS.PARAGRAPH]: (_, children) => [children, <br key="line-break" />],
            [INLINES.EMBEDDED_ENTRY]: node => {
              if (node.data.target.sys.contentType.sys.id === 'inlineFootnote') {
                return <ContentfulInlineFootnote component={node.data.target} analyticsLocation={plainTextHeading} />
              }

              return null
            },
          },
        })}
      </Pillar.Description>

      {link && (
        <Pillar.Link
          href={link.fields.href}
          data-ref={`pillar-link-${link.sys.id}`}
          {...getAnalyticsEvent({
            action: link.fields.text,
            tag: 'link',
            context: plainTextHeading,
          })}
        >
          {link.fields.text}
        </Pillar.Link>
      )}
    </Pillar>
  )
}

export const ContentfulPillar = ({component, className}: ContentfulPillarProps) => {
  return <ContentfulPillarContent component={component} className={className} />
}
